import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Search, Trash2, AlertTriangle, Database, HardDrive, History, RefreshCw, File, Image, FileText, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";

interface StorageFileInfo {
  bucket: string;
  path: string;
  name: string;
  size: number;
  createdAt: string;
  mimeType: string | null;
}

interface OrphanResult {
  tableCounts: Record<string, number>;
  storageCounts: Record<string, number>;
  authIdentitiesCount: number;
  sampleIds: Record<string, string[]>;
  storageFiles: StorageFileInfo[];
  errors: string[];
}

export function OrphanCleanupManager() {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<"db" | "storage" | "both">("both");
  const [olderThanDays, setOlderThanDays] = useState(7);
  const [limit, setLimit] = useState(1000);
  const [scanResult, setScanResult] = useState<OrphanResult | null>(null);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<StorageFileInfo | null>(null);

  // Fetch cleanup audit log
  const { data: cleanupLogs, isLoading: logsLoading } = useQuery({
    queryKey: ["cleanup_audit_logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_cleanup_audit")
        .select("*")
        .order("ran_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    },
  });

  // Scan mutation
  const scanMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("orphan-cleanup", {
        body: {
          action: "scan",
          scope,
          olderThanDays,
          limit,
        },
      });

      if (response.error) throw response.error;
      return response.data as OrphanResult;
    },
    onSuccess: (data) => {
      setScanResult(data);
      setSelectedFiles(new Set());
      queryClient.invalidateQueries({ queryKey: ["cleanup_audit_logs"] });
      
      const totalOrphans = Object.values(data.tableCounts).reduce((a, b) => a + b, 0) +
        Object.values(data.storageCounts).reduce((a, b) => a + b, 0);
      
      if (totalOrphans > 0) {
        toast.info(`Found ${totalOrphans} orphaned records/files`);
      } else {
        toast.success("No orphaned data found");
      }
    },
    onError: (error) => {
      toast.error(`Scan failed: ${error.message}`);
    },
  });

  // Purge mutation
  const purgeMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("orphan-cleanup", {
        body: {
          action: "purge",
          scope,
          olderThanDays,
          limit,
        },
      });

      if (response.error) throw response.error;
      return response.data as OrphanResult;
    },
    onSuccess: (data) => {
      setScanResult(data);
      setShowPurgeDialog(false);
      setConfirmText("");
      setSelectedFiles(new Set());
      queryClient.invalidateQueries({ queryKey: ["cleanup_audit_logs"] });
      
      const totalPurged = Object.values(data.tableCounts).reduce((a, b) => a + b, 0) +
        Object.values(data.storageCounts).reduce((a, b) => a + b, 0);
      
      toast.success(`Purged ${totalPurged} orphaned records/files`);
    },
    onError: (error) => {
      toast.error(`Purge failed: ${error.message}`);
    },
  });

  // Delete individual file mutation
  const deleteFileMutation = useMutation({
    mutationFn: async ({ bucket, filePath }: { bucket: string; filePath: string }) => {
      const response = await supabase.functions.invoke("orphan-cleanup", {
        body: {
          action: "delete-file",
          bucket,
          filePath,
        },
      });

      if (response.error) throw response.error;
      return { bucket, filePath };
    },
    onSuccess: ({ filePath }) => {
      // Remove from scan results
      if (scanResult) {
        setScanResult({
          ...scanResult,
          storageFiles: scanResult.storageFiles.filter(f => f.path !== filePath),
        });
      }
      setDeletingFiles(prev => {
        const next = new Set(prev);
        next.delete(filePath);
        return next;
      });
      setSelectedFiles(prev => {
        const next = new Set(prev);
        next.delete(filePath);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["cleanup_audit_logs"] });
      toast.success("File deleted successfully");
    },
    onError: (error, { filePath }) => {
      setDeletingFiles(prev => {
        const next = new Set(prev);
        next.delete(filePath);
        return next;
      });
      toast.error(`Failed to delete file: ${error.message}`);
    },
  });

  const handleDeleteFile = (file: StorageFileInfo) => {
    setDeletingFiles(prev => new Set(prev).add(file.path));
    deleteFileMutation.mutate({ bucket: file.bucket, filePath: file.path });
  };

  const handleDeleteSelected = async () => {
    if (selectedFiles.size === 0) return;
    
    const filesToDelete = scanResult?.storageFiles.filter(f => selectedFiles.has(f.path)) || [];
    
    for (const file of filesToDelete) {
      setDeletingFiles(prev => new Set(prev).add(file.path));
      await deleteFileMutation.mutateAsync({ bucket: file.bucket, filePath: file.path });
    }
  };

  const handlePurge = () => {
    if (confirmText !== "PURGE") {
      toast.error("Please type PURGE to confirm");
      return;
    }
    purgeMutation.mutate();
  };

  const toggleSelectAll = () => {
    if (!scanResult) return;
    
    if (selectedFiles.size === scanResult.storageFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(scanResult.storageFiles.map(f => f.path)));
    }
  };

  const toggleFileSelection = (path: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const getFileIcon = (mimeType: string | null) => {
    if (!mimeType) return <File className="h-4 w-4 text-muted-foreground" />;
    if (mimeType.startsWith("image/")) return <Image className="h-4 w-4 text-primary" />;
    if (mimeType.includes("pdf")) return <FileText className="h-4 w-4 text-destructive" />;
    return <File className="h-4 w-4 text-muted-foreground" />;
  };

  const getFilePreviewUrl = (file: StorageFileInfo): string | null => {
    if (!file.mimeType?.startsWith("image/")) return null;
    const { data } = supabase.storage.from(file.bucket).getPublicUrl(file.path);
    return data.publicUrl;
  };

  const totalOrphans = scanResult
    ? Object.values(scanResult.tableCounts).reduce((a, b) => a + b, 0) +
      Object.values(scanResult.storageCounts).reduce((a, b) => a + b, 0) +
      (scanResult.authIdentitiesCount || 0)
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Orphan Data Cleanup
          </CardTitle>
          <CardDescription>
            Scan and purge orphaned database records and storage objects from deleted users or failed operations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Configuration */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Scope</Label>
              <RadioGroup value={scope} onValueChange={(v) => setScope(v as typeof scope)}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="both" id="scope-both" />
                  <Label htmlFor="scope-both" className="font-normal">DB + Storage</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="db" id="scope-db" />
                  <Label htmlFor="scope-db" className="font-normal">Database Only</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="storage" id="scope-storage" />
                  <Label htmlFor="scope-storage" className="font-normal">Storage Only</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="olderThan">Older Than (Days)</Label>
              <Input
                id="olderThan"
                type="number"
                min={1}
                max={365}
                value={olderThanDays}
                onChange={(e) => setOlderThanDays(parseInt(e.target.value) || 7)}
              />
              <p className="text-xs text-muted-foreground">Only scan records created before this many days ago</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="limit">Limit</Label>
              <Input
                id="limit"
                type="number"
                min={100}
                max={10000}
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value) || 1000)}
              />
              <p className="text-xs text-muted-foreground">Maximum records to process per table</p>
            </div>
          </div>

          {/* Scan Button */}
          <div className="flex gap-2">
            <Button onClick={() => scanMutation.mutate()} disabled={scanMutation.isPending}>
              {scanMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Scan for Orphans
                </>
              )}
            </Button>

            {scanResult && totalOrphans > 0 && (
              <Button
                variant="destructive"
                onClick={() => setShowPurgeDialog(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Purge All Orphans
              </Button>
            )}
          </div>

          {/* Scan Results */}
          {scanResult && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Scan Results</h4>
                <Badge variant={totalOrphans > 0 ? "destructive" : "secondary"}>
                  {totalOrphans} orphaned items found
                </Badge>
              </div>

              {Object.keys(scanResult.tableCounts).length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    <span className="font-medium text-sm">Database Records</span>
                  </div>
                  <div className="grid gap-2 pl-6">
                    {Object.entries(scanResult.tableCounts).map(([table, count]) => (
                      <div key={table} className="flex items-center justify-between p-2 bg-muted rounded">
                        <span className="text-sm font-mono">{table}</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{count} rows</Badge>
                          {scanResult.sampleIds[table] && (
                            <span className="text-xs text-muted-foreground">
                              e.g., {scanResult.sampleIds[table][0]?.slice(0, 8)}...
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Detailed Storage Files Table */}
              {scanResult.storageFiles && scanResult.storageFiles.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HardDrive className="h-4 w-4" />
                      <span className="font-medium text-sm">Storage Files ({scanResult.storageFiles.length})</span>
                    </div>
                    {selectedFiles.size > 0 && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleDeleteSelected}
                        disabled={deletingFiles.size > 0}
                      >
                        {deletingFiles.size > 0 ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-1" />
                        )}
                        Delete Selected ({selectedFiles.size})
                      </Button>
                    )}
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={selectedFiles.size === scanResult.storageFiles.length && scanResult.storageFiles.length > 0}
                                onCheckedChange={toggleSelectAll}
                              />
                            </TableHead>
                            <TableHead>File</TableHead>
                            <TableHead>Bucket</TableHead>
                            <TableHead className="text-right">Size</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead className="w-24">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scanResult.storageFiles.map((file) => (
                            <TableRow key={file.path}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedFiles.has(file.path)}
                                  onCheckedChange={() => toggleFileSelection(file.path)}
                                  disabled={deletingFiles.has(file.path)}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {getFileIcon(file.mimeType)}
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate max-w-[200px]" title={file.name}>
                                      {file.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate max-w-[200px]" title={file.path}>
                                      {file.path}
                                    </p>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{file.bucket}</Badge>
                              </TableCell>
                              <TableCell className="text-right text-sm">
                                {formatFileSize(file.size)}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {file.createdAt ? format(new Date(file.createdAt), "PP") : "—"}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {file.mimeType?.startsWith("image/") && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8"
                                      onClick={() => setPreviewFile(file)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  )}
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    onClick={() => handleDeleteFile(file)}
                                    disabled={deletingFiles.has(file.path)}
                                  >
                                    {deletingFiles.has(file.path) ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>
                </div>
              )}

              {/* Fallback for bucket counts if no detailed files */}
              {Object.keys(scanResult.storageCounts).length > 0 && (!scanResult.storageFiles || scanResult.storageFiles.length === 0) && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-4 w-4" />
                    <span className="font-medium text-sm">Storage Objects</span>
                  </div>
                  <div className="grid gap-2 pl-6">
                    {Object.entries(scanResult.storageCounts).map(([bucket, count]) => (
                      <div key={bucket} className="flex items-center justify-between p-2 bg-muted rounded">
                        <span className="text-sm font-mono">{bucket}</span>
                        <Badge variant="outline">{count} files</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {scanResult.errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-medium mb-1">Scan Errors:</p>
                    <ul className="list-disc list-inside text-sm">
                      {scanResult.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {scanResult.authIdentitiesCount > 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <p className="font-medium">Found {scanResult.authIdentitiesCount} orphaned auth identity records!</p>
                    <p className="text-sm mt-1">
                      These records may prevent deleted users from re-registering with their email addresses.
                      <strong> Purging is strongly recommended.</strong>
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {totalOrphans === 0 && (
                <Alert>
                  <RefreshCw className="h-4 w-4" />
                  <AlertDescription>
                    No orphaned data found matching your criteria. Your database is clean!
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cleanup Audit Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Cleanup Audit Log
          </CardTitle>
          <CardDescription>
            Recent scan and purge operations (no PII stored)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : cleanupLogs && cleanupLogs.length > 0 ? (
            <ScrollArea className="h-[250px]">
              <div className="space-y-2">
                {cleanupLogs.map((log) => (
                  <div key={log.id} className="p-3 border rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={log.action === "purge" ? "destructive" : log.action === "delete-file" ? "outline" : "secondary"}>
                          {log.action}
                        </Badge>
                        <Badge variant="outline">{log.scope}</Badge>
                      </div>
                      <span className="text-muted-foreground">
                        {format(new Date(log.ran_at), "PPp")}
                      </span>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                      {log.action === "delete-file" ? (
                        <span>Deleted: {(log.params as Record<string, string>)?.filePath || "—"}</span>
                      ) : (
                        <>
                          <span>Older than: {(log.params as Record<string, number>)?.olderThanDays || 7} days</span>
                          <span>
                            Tables: {Object.values((log.results as Record<string, Record<string, number>>)?.tableCounts || {}).reduce((a, b) => a + b, 0)}
                          </span>
                          <span>
                            Storage: {Object.values((log.results as Record<string, Record<string, number>>)?.storageCounts || {}).reduce((a, b) => a + b, 0)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-center text-muted-foreground py-8">No cleanup operations recorded yet</p>
          )}
        </CardContent>
      </Card>

      {/* Purge Confirmation Dialog */}
      <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Confirm Orphan Purge
            </DialogTitle>
            <DialogDescription>
              This will permanently delete {totalOrphans} orphaned records and files.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> Only proceed if you have verified these are truly orphaned
              records that should not exist. When in doubt, keep the data.
            </AlertDescription>
          </Alert>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <span>Database Records:</span>
                <span className="font-medium">
                  {Object.values(scanResult?.tableCounts || {}).reduce((a, b) => a + b, 0)}
                </span>
                <span>Storage Objects:</span>
                <span className="font-medium">
                  {Object.values(scanResult?.storageCounts || {}).reduce((a, b) => a + b, 0)}
                </span>
                <span>Auth Identities:</span>
                <span className="font-medium">
                  {scanResult?.authIdentitiesCount || 0}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Type PURGE to confirm</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="PURGE"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurgeDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handlePurge}
              disabled={confirmText !== "PURGE" || purgeMutation.isPending}
            >
              {purgeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Purging...
                </>
              ) : (
                "Confirm Purge"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="h-5 w-5" />
              {previewFile?.name}
            </DialogTitle>
            <DialogDescription>
              {previewFile?.path} • {previewFile && formatFileSize(previewFile.size)}
            </DialogDescription>
          </DialogHeader>
          {previewFile && (
            <div className="flex justify-center">
              <img
                src={getFilePreviewUrl(previewFile) || ""}
                alt={previewFile.name}
                className="max-h-[60vh] rounded-lg object-contain"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewFile(null)}>
              Close
            </Button>
            {previewFile && (
              <Button
                variant="destructive"
                onClick={() => {
                  handleDeleteFile(previewFile);
                  setPreviewFile(null);
                }}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete File
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
